/**
 * Widget layout — per-user customization for hub pages.
 *
 * Model: a single ordered list of widgets. Each widget has:
 *   - an id (stable)
 *   - a width ('half' = lives in the balanced two-up flow | 'full' = spans the row)
 *   - an order (its index in the list)
 *   - a visibility flag
 *
 * Render (handled by WidgetCanvas):
 *   - walk the ordered list top-to-bottom
 *   - a 'full' widget renders as its own full-width row
 *   - consecutive 'half' widgets render in an auto-balancing block so columns
 *     stay even height with zero manual fiddling
 *
 * State persists to localStorage under a hub-specific key. Backward-compatible:
 * legacy layouts (single-list `order`, or column-aware `placements`) are
 * migrated to the ordered {id, width} shape on first read — column info is
 * collapsed into a single order (left column first, then right) and every
 * migrated widget starts 'half'.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

export type Width = 'half' | 'full';

/** @deprecated columns were replaced by the ordered+width model; kept for callers passing old defaults. */
export type Column = 'left' | 'right';

/** Default-layout shape — caller declares each widget's starting width. */
export interface DefaultWidget {
  id: string;
  /** Starting width. Defaults to 'half'. */
  width?: Width;
  /** @deprecated legacy column hint — used only to derive initial order. */
  column?: Column;
  /** If true, widget is hidden by default (lives in the "+ chip" rail). */
  hiddenByDefault?: boolean;
}

interface Stored {
  // Ordered [id, width] tuples. Array order IS the render order.
  items: Array<[string, Width]>;
  hidden: string[];
}

const KEY_PREFIX = 'waypoint:widget-layout:';

function migrateLegacy(parsed: Record<string, unknown>): Stored | null {
  // v2 format: { placements: [id, column][], hidden }
  if (Array.isArray(parsed.placements)) {
    const placements = parsed.placements as Array<[string, string]>;
    const left = placements.filter(p => p[1] === 'left').map(p => p[0]);
    const right = placements.filter(p => p[1] === 'right').map(p => p[0]);
    const order = [...left, ...right];
    return {
      items: order.map(id => [id, 'half'] as [string, Width]),
      hidden: Array.isArray(parsed.hidden) ? (parsed.hidden as unknown[]).filter((x): x is string => typeof x === 'string') : [],
    };
  }
  // v1 format: { order: string[], hidden }
  if (Array.isArray(parsed.order)) {
    return {
      items: (parsed.order as string[]).map(id => [id, 'half'] as [string, Width]),
      hidden: Array.isArray(parsed.hidden) ? (parsed.hidden as unknown[]).filter((x): x is string => typeof x === 'string') : [],
    };
  }
  return null;
}

function readStored(hubKey: string): Stored | null {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + hubKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed) return null;
    if (Array.isArray(parsed.items)) {
      return {
        items: parsed.items.filter(
          (p: unknown): p is [string, Width] =>
            Array.isArray(p) && typeof p[0] === 'string' && (p[1] === 'half' || p[1] === 'full'),
        ),
        hidden: Array.isArray(parsed.hidden) ? parsed.hidden.filter((x: unknown) => typeof x === 'string') : [],
      };
    }
    return migrateLegacy(parsed);
  } catch {
    return null;
  }
}

function writeStored(hubKey: string, state: Stored) {
  try {
    localStorage.setItem(KEY_PREFIX + hubKey, JSON.stringify(state));
  } catch {
    /* quota / private-mode — best effort */
  }
}

export interface UseWidgetLayout {
  /** Visible widget ids in render order. */
  orderedIds: string[];
  /** Hidden widget ids. */
  hiddenIds: string[];
  /** Alias for orderedIds — back-compat for single-column callers. */
  visibleIds: string[];
  /** Width of a widget ('half' default). */
  widthOf: (id: string) => Width;
  /** Is this widget visible? */
  isVisible: (id: string) => boolean;
  /** Toggle a widget between half and full width. */
  toggleWidth: (id: string) => void;
  /** Move a widget up/down one slot in the order (keyboard / button fallback). */
  move: (id: string, direction: 'up' | 'down') => void;
  /** Drag-and-drop primary mutation: place `id` before `beforeId` (or at end if null). */
  reorder: (id: string, beforeId: string | null) => void;
  /** Toggle visibility. */
  toggle: (id: string) => void;
  /** Reset to defaults. */
  reset: () => void;
  /** Have the defaults been changed from? */
  isCustomized: boolean;
}

type DefaultLayout = DefaultWidget[] | string[];

function normaliseDefaults(defaults: DefaultLayout): Array<{ id: string; width: Width; hiddenByDefault?: boolean }> {
  if (defaults.length === 0) return [];
  if (typeof defaults[0] === 'string') {
    return (defaults as string[]).map(id => ({ id, width: 'half' as Width }));
  }
  // Preserve declared order. If callers used the old `column` field, left-column
  // widgets sort ahead of right-column ones so the initial order reads naturally.
  const arr = defaults as DefaultWidget[];
  const usesColumns = arr.some(d => d.column);
  const ordered = usesColumns
    ? [...arr.filter(d => d.column !== 'right'), ...arr.filter(d => d.column === 'right')]
    : arr;
  return ordered.map(d => ({ id: d.id, width: d.width ?? 'half', hiddenByDefault: d.hiddenByDefault }));
}

export function useWidgetLayout(
  hubKey: string,
  defaultLayout: DefaultLayout,
): UseWidgetLayout {
  const defaults = useMemo(() => normaliseDefaults(defaultLayout), [defaultLayout]);

  const [state, setState] = useState<Stored>(() => {
    const stored = readStored(hubKey);
    const defaultHidden = defaults.filter(d => d.hiddenByDefault).map(d => d.id);
    if (!stored) {
      return { items: defaults.map(d => [d.id, d.width] as [string, Width]), hidden: defaultHidden };
    }
    const known = new Set(defaults.map(d => d.id));
    const filtered = stored.items.filter(([id]) => known.has(id));
    const present = new Set(filtered.map(([id]) => id));
    const newlyAddedHidden: string[] = [];
    for (const d of defaults) {
      if (!present.has(d.id)) {
        filtered.push([d.id, d.width]);
        if (d.hiddenByDefault) newlyAddedHidden.push(d.id);
      }
    }
    const hidden = Array.from(new Set([
      ...stored.hidden.filter(id => known.has(id)),
      ...newlyAddedHidden,
    ]));
    return { items: filtered, hidden };
  });

  useEffect(() => { writeStored(hubKey, state); }, [hubKey, state]);

  const reorder = useCallback((id: string, beforeId: string | null) => {
    setState(prev => {
      const item = prev.items.find(([pid]) => pid === id);
      if (!item) return prev;
      const without = prev.items.filter(([pid]) => pid !== id);
      if (beforeId === null) return { ...prev, items: [...without, item] };
      const idx = without.findIndex(([pid]) => pid === beforeId);
      if (idx < 0) return { ...prev, items: [...without, item] };
      const next = [...without];
      next.splice(idx, 0, item);
      return { ...prev, items: next };
    });
  }, []);

  const move = useCallback((id: string, direction: 'up' | 'down') => {
    setState(prev => {
      const idx = prev.items.findIndex(([pid]) => pid === id);
      if (idx < 0) return prev;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.items.length) return prev;
      const next = [...prev.items];
      const tmp = next[idx]!;
      next[idx] = next[target]!;
      next[target] = tmp;
      return { ...prev, items: next };
    });
  }, []);

  const toggleWidth = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      items: prev.items.map(([pid, w]) => (pid === id ? [pid, w === 'full' ? 'half' : 'full'] : [pid, w]) as [string, Width]),
    }));
  }, []);

  const toggle = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      hidden: prev.hidden.includes(id) ? prev.hidden.filter(x => x !== id) : [...prev.hidden, id],
    }));
  }, []);

  const reset = useCallback(() => {
    setState({ items: defaults.map(d => [d.id, d.width] as [string, Width]), hidden: [] });
  }, [defaults]);

  const hiddenSet = new Set(state.hidden);
  const orderedIds = state.items.filter(([id]) => !hiddenSet.has(id)).map(([id]) => id);
  const hiddenIds = state.items.filter(([id]) => hiddenSet.has(id)).map(([id]) => id);
  const widthMap = new Map(state.items);

  const defaultSerialized = JSON.stringify(defaults.map(d => [d.id, d.width]));
  const isCustomized = state.hidden.length > 0 || JSON.stringify(state.items) !== defaultSerialized;

  return {
    orderedIds,
    visibleIds: orderedIds,
    hiddenIds,
    widthOf: (id) => widthMap.get(id) ?? 'half',
    isVisible: (id) => !hiddenSet.has(id),
    toggleWidth,
    move,
    reorder,
    toggle,
    reset,
    isCustomized,
  };
}
